import ConfigStore from './ConfigStore';
import OrderStore from './OrderStore';
import HallStore from './HallStore';
import StyleStore from './StyleStore';
import PendingAnimationStore from './PendingAnimationStore'
import {action} from 'mobx';
import axios from 'axios';
import { HALL_PAGE } from '../constants';
import * as Sentry from '@sentry/browser';

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_API_BASE_URL,
    timeout: 12000
});

const PULL_CONFIG_DELAY_MS = 2000;
const REQUEST_TIMEOUT_DELAY = 1500;
const DELAY_AFTER_ORDERS_COMPLETE = 5 * 60 * 1000;


export default class RootStore {
    pendingCheckinAnimationStore; // Declare property for intellisense purposes

    constructor() {
        this.configStore = new ConfigStore();
        this.orderStore = new OrderStore();
        this.hallStore = new HallStore();
        this.styleStore = new StyleStore(this.configStore);
        this.pendingCheckinAnimationStore = new PendingAnimationStore();
        this.closeShopTimer = null;
    }

    async fetchConfig() {
        let path = `/api/configuration?display_id=${this.configStore.display_id}`;
        return axiosInstance.get(path)
    }

    async fetchShops() {
        let path = `/api/shop/list?display_id=${this.configStore.display_id}`;
        return axiosInstance.get(path)
    }

    async fetchOrders() {
        let path = `/api/shop/data?display_id=${this.configStore.display_id}`;
        return axiosInstance.get(path)
    }

    pullData = async () => {
        try {
            const promises = [this.fetchShops(), this.configStore.currentPage !== HALL_PAGE ? this.fetchOrders():''];
            let [{data: shopData}, {data: orderData}] = await Promise.all(promises);
            //log the data coming in (will not appear in prod, see webpack configs)
            console.log('The ShopList Grabbed:', shopData);
            console.log('The Data Grabbed:', orderData);

            //if we get here than the Promises completed, and we can now write ALL the data
            this.configStore.setIsTVEnabled(shopData.is_enabled)
            this.hallStore.setHallData(shopData);
            this.setConfigAndOrderData(orderData);
            this.configStore.updateReceivedResponse();
            if(this.configStore.firstTick) {
                this.configStore.turnOffLoadingScreen();
                this.configStore.toggleFirstClick();
            }
            //if we succeed we write call the function again after the delay.
            setTimeout(this.pullData, REQUEST_TIMEOUT_DELAY);
        } catch (e) {
            //in case the request fails, wait a second, and then pull again
            Sentry.captureException(e);
            setTimeout(this.pullData, REQUEST_TIMEOUT_DELAY);
        }
    }

    //This is the main entry point for the app!
    initializeTimer = null;
    @action async initialize() {
        if (this.initializeTimer) {
            clearTimeout(this.initializeTimer)
        }
        try {
            this.initializeTimer = await this.fetchConfig();
            this.configStore.setCurrentPage(this.initializeTimer.data.screen_type);
            this.configStore.setDesignType(this.initializeTimer.data.design_type);
            this.styleStore.updateStyleName(this.initializeTimer.data.design_type);
            this.configStore.setPromo(this.initializeTimer.data.promo);
            this.configStore.setIsTVEnabled(this.initializeTimer.data.is_enabled);
        } catch (e) {
            let This = this;
            setTimeout(()=>{
                This.initialize();
            }, PULL_CONFIG_DELAY_MS);
            return;
        }
        this.pullData();
        this.inititiateCheckResponse();
    }

    @action inititiateCheckResponse() {
        setInterval(()=>{
            this.configStore.checkConnection();
        }, this.configStore.checkResponseInterval);
    }

    @action resetCloseShopTimer() {
        clearTimeout(this.closeShopTimer);
        this.closeShopTimer = null;
    }

    @action leaveOpenUntilAllOrdersComplete(orders) {
        // a timer to close the shop was already set
        if (this.closeShopTimer) {
            return;
        }

        // let customers know shop is closed
        this.configStore.setShopClosedWithOrders(true);

        // while orders are incomplete, leave the shop open
        if (orders.some(order => order.status !== 'ready')) {
            this.configStore.setIsShopOpen(true);
            return;
        }

        // close the shop 5 minutes after all orders are complete
        this.closeShopTimer = setTimeout(() => {
            this.configStore.setIsShopOpen(false);
            this.configStore.setShopClosedWithOrders(false);
        }, DELAY_AFTER_ORDERS_COMPLETE)
    }

    @action setShopActivityConfig(isOpen, turnOffAfterWorkingHoursFlag) {
        const orders = Object.values(this.orderStore.ordersDict);

        if (isOpen) {
            this.resetCloseShopTimer();
            this.configStore.setShopClosedWithOrders(false);
            this.configStore.setIsShopOpen(true);
            return;
        }

        if (turnOffAfterWorkingHoursFlag || orders.length === 0) {
            this.resetCloseShopTimer();
            this.configStore.setShopClosedWithOrders(false);
            this.configStore.setIsShopOpen(false);
            return;
        }

        this.leaveOpenUntilAllOrdersComplete(orders);
    }

    @action setConfigAndOrderData(orderData) {
        if(!orderData) {
            return;
        }
        orderData.orders = orderData.orders.map(order => {
            order.pendingCheckin = order.shop_supports_check_in && !order.checked_in;
            order.pendingRefund = order.ac_state === 'pending_refund';
            return order;
        });
        //update rowsCountsColsCount and use_names
        this.orderStore.updateColCount(orderData.config.cols_count);
        this.orderStore.updateRowCount(orderData.config.rows_count);
        this.configStore.setUseNames(orderData.config.use_initials);
        this.configStore.setPromo(orderData.config.promo);
        this.styleStore.updateStyleName(orderData.config.design_type);

        let shop_info = orderData.shop_info;
        this.configStore.setUnderMaintence(shop_info.is_hw_um);
        this.configStore.setOfflineMode(shop_info.is_offline_mode_enabled, shop_info.is_hw_um);
        this.orderStore.updateMainShopData(shop_info.name, shop_info.image, shop_info.eta, shop_info.wh, shop_info.brand,
          shop_info.hide_eta, shop_info.id)
        this.configStore.setNextShopStateChange(shop_info.next_shop_state_change)
        this.configStore.updateDateTime(shop_info.current_shop_time);
        this.configStore.setQrCode(orderData.config.qr_code);
        this.configStore.calcIsVertical();
        this.configStore.setTurnOffCdsAfterWorkingHoursFlag(shop_info.turn_off_cds_after_working_hours);
        this.orderStore.updateQueue(this.configStore.displayOfflineMode ? [] : orderData.orders);
        this.setShopActivityConfig(shop_info.is_open, shop_info.turn_off_cds_after_working_hours);
    }

    generateGrubhubAppDeepLink = () => {
        return `grubhubapp://checkin?code=${this.configStore.qrCode}&shop_id=-${this.orderStore.mainShopData.id}`;
    }
}



// WEBPACK FOOTER //
// ./src/stores/RootStore.js